import { z } from 'zod';
import { ROLES, ESTADOS_RECETA, ESTADOS_MINUTA, ESTADOS_PEDIDO, ESTADOS_COMPRA, CATEGORIAS_INGREDIENTE, CATEGORIAS_RECETA, TIPOS_COMIDA, UNIDADES_MEDIDA, } from '../constants';
// Schemas para enums
export const RolSchema = z.enum(Object.values(ROLES));
export const EstadoRecetaSchema = z.enum(Object.values(ESTADOS_RECETA));
export const EstadoMinutaSchema = z.enum(Object.values(ESTADOS_MINUTA));
export const EstadoPedidoSchema = z.enum(Object.values(ESTADOS_PEDIDO));
export const EstadoCompraSchema = z.enum(Object.values(ESTADOS_COMPRA));
export const CategoriaIngredienteSchema = z.enum(Object.values(CATEGORIAS_INGREDIENTE));
export const CategoriaRecetaSchema = z.enum(Object.values(CATEGORIAS_RECETA));
export const TipoComidaSchema = z.enum(Object.values(TIPOS_COMIDA));
export const UnidadMedidaSchema = z.enum(Object.values(UNIDADES_MEDIDA));
// Schema para Usuario
export const UsuarioSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(255),
    email: z.string().email().max(255),
    password_hash: z.string().min(1),
    rol: RolSchema,
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearUsuarioSchema = z.object({
    nombre: z.string().min(1).max(255),
    email: z.string().email().max(255),
    password: z.string().min(6),
    rol: RolSchema,
    activo: z.boolean().default(true),
});
// Schema para Ingrediente
export const IngredienteSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(255),
    descripcion: z.string().max(500).optional(),
    categoria: CategoriaIngredienteSchema,
    unidad_medida: UnidadMedidaSchema,
    costo_unitario: z.number().positive(),
    stock_actual: z.number().min(0),
    stock_minimo: z.number().min(0),
    stock_maximo: z.number().positive(),
    proveedor: z.string().max(255).optional(),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearIngredienteSchema = z.object({
    nombre: z.string().min(1).max(255),
    descripcion: z.string().max(500).optional(),
    categoria: CategoriaIngredienteSchema,
    unidad_medida: UnidadMedidaSchema,
    costo_unitario: z.number().positive(),
    stock_actual: z.number().min(0).default(0),
    stock_minimo: z.number().min(0).default(0),
    stock_maximo: z.number().positive(),
    proveedor: z.string().max(255).optional(),
    activo: z.boolean().default(true),
});
// Schema para Receta
export const RecetaSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(255),
    descripcion: z.string().max(1000).optional(),
    categoria: CategoriaRecetaSchema,
    tipo_comida: TipoComidaSchema,
    porciones: z.number().int().positive(),
    tiempo_preparacion: z.number().int().positive(),
    instrucciones: z.string().min(1),
    costo_total: z.number().min(0),
    estado: EstadoRecetaSchema,
    chef_id: z.number().int().positive(),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearRecetaSchema = z.object({
    nombre: z.string().min(1).max(255),
    descripcion: z.string().max(1000).optional(),
    categoria: CategoriaRecetaSchema,
    tipo_comida: TipoComidaSchema,
    porciones: z.number().int().positive(),
    tiempo_preparacion: z.number().int().positive(),
    instrucciones: z.string().min(1),
    chef_id: z.number().int().positive(),
    estado: EstadoRecetaSchema.default('borrador'),
    activo: z.boolean().default(true),
});
// Schema para tags de receta (vegetariano, vegano, sin gluten, …).
// El catálogo vive en la tabla tags_receta. Cada tag tiene:
//   slug   — identificador kebab-lowercase sin tildes (filtros / URLs)
//   nombre — display amigable con tildes y mayúscula (UI)
export const TagRecetaSchema = z.object({
    id: z.number().int().positive(),
    slug: z.string().min(1).max(100),
    nombre: z.string().min(1).max(100),
    descripcion: z.string().max(1000).nullable().optional(),
    activo: z.boolean().default(true),
});
// Asignación de tags al crear/actualizar una receta. Si se omite en un
// PATCH, los tags actuales no se tocan; si se envía [], se limpian.
export const AsignarTagsRecetaSchema = z.object({
    tags: z.array(z.number().int().positive()).optional(),
});
// === Modelo de opciones por minuta (con `rol`) ===
// Convención: ver db/migrations/2026-04-26_modelo_opciones_minuta_con_rol.sql
//   fila_orden = número de OPCIÓN dentro de la celda (1, 2, ...).
//   rol        = 'unico' (tipos simples) | 'fondo' | 'guarnicion'
//                (tipos combinados id 3 y 10).
export const RolMinutaDetalleSchema = z.enum([
    'unico',
    'fondo',
    'guarnicion',
]);
export const AsignarRecetaMinutaSchema = z.object({
    fecha_especifica: z.string().min(1),
    tipo_plato_id: z.number().int().positive(),
    receta_id: z.number().int().positive(),
    fila_orden: z.number().int().positive().optional().default(1),
    rol: RolMinutaDetalleSchema.optional().default('unico'),
});
export const RemoverRecetaMinutaSchema = z.object({
    fecha_especifica: z.string().min(1),
    tipo_plato_id: z.number().int().positive(),
    fila_orden: z.number().int().positive().optional().default(1),
    rol: RolMinutaDetalleSchema.optional().default('unico'),
});
// Schema para IngredienteReceta
export const IngredienteRecetaSchema = z.object({
    id: z.number().int().positive(),
    receta_id: z.number().int().positive(),
    ingrediente_id: z.number().int().positive(),
    cantidad: z.number().positive(),
    unidad: UnidadMedidaSchema,
    costo_parcial: z.number().min(0),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearIngredienteRecetaSchema = z.object({
    receta_id: z.number().int().positive(),
    ingrediente_id: z.number().int().positive(),
    cantidad: z.number().positive(),
    unidad: UnidadMedidaSchema,
});
// Schema para Minuta
export const MinutaSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(255),
    descripcion: z.string().max(1000).optional(),
    fecha_inicio: z.date(),
    fecha_fin: z.date(),
    estado: EstadoMinutaSchema,
    creado_por: z.number().int().positive(),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearMinutaSchema = z
    .object({
    nombre: z.string().min(1).max(255),
    descripcion: z.string().max(1000).optional(),
    fecha_inicio: z.date(),
    fecha_fin: z.date(),
    creado_por: z.number().int().positive(),
    estado: EstadoMinutaSchema.default('borrador'),
    activo: z.boolean().default(true),
})
    .refine((data) => data.fecha_fin >= data.fecha_inicio, {
    message: 'La fecha de fin debe ser posterior a la fecha de inicio',
    path: ['fecha_fin'],
});
// Schema para tipos de cliente
export const TipoClienteSchema = z.enum(['persona_natural', 'juridica']);
// === Modelo v2 de categorias_acceso (contrato granular por cliente) ===
// Ver db/migrations/2026-06-10_tipos_plato_bloque.sql + sesión 2026-06-10.
//
// Shape canónico (v2):
//   {
//     tipos:     number[]                              // tipos_plato.id permitidos
//     opciones?: Record<string, number[]>              // por tipo combinado (CP=3, CENA=10),
//                                                     //   qué fila_orden (opción 1, 2, …)
//                                                     //   está permitida. Sin entrada => TODAS
//                                                     //   las filas de ese tipo.
//     bebidas?:  number[]                              // recetas BEBIDA (tipo_plato_id=11)
//                                                     //   permitidas. Solo aplica si 11 ∈ tipos.
//                                                     //   Sin bebidas => TODAS las recetas
//                                                     //   BEBIDA activas.
//   }
//
// Retrocompatibilidad: si llega `number[]` legacy, se interpreta como `{ tipos: number[] }`
//                      (sin restricciones de opciones/bebidas).
export const CategoriasAccesoV2Schema = z.object({
    tipos: z.array(z.number().int().positive()),
    opciones: z
        .record(z.string(), z.array(z.number().int().positive()))
        .optional(),
    bebidas: z.array(z.number().int().positive()).optional(),
});
export const CategoriasAccesoInputSchema = z.union([
    z
        .array(z.number().int().positive())
        .transform((arr) => ({ tipos: arr })),
    CategoriasAccesoV2Schema,
]);
/**
 * Normaliza cualquier entrada (legacy number[], objeto v2, o string JSON
 * recién leído de MySQL) al shape v2 canónico. Devuelve null si no parsea
 * (deja la decisión al caller — backend semánticamente trata null como
 * "sin acceso" en el filtro de Armar Pedido).
 */
export function parseCategoriasAccesoV2(raw) {
    if (raw === null || raw === undefined)
        return null;
    let value = raw;
    if (typeof raw === 'string') {
        try {
            value = JSON.parse(raw);
        }
        catch {
            return null;
        }
    }
    const parsed = CategoriasAccesoInputSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
// Schema para Cliente (expandido)
export const ClienteSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(100),
    rut: z.string().min(1).max(20),
    email: z.string().email().max(100),
    nombre_contacto: z.string().min(1).max(100),
    telefono_contacto: z.string().max(20).optional(),
    tipo_cliente: TipoClienteSchema,
    razon_social: z.string().max(255).optional(),
    representante_legal: z.string().max(100).optional(),
    observaciones_despacho: z.string().optional(),
    categorias_acceso: z.array(z.number().int().positive()).optional(),
    usuario_id: z.number().int().positive().optional(),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearClienteSchema = z
    .object({
    nombre: z.string().min(1).max(100),
    rut: z.string().min(1).max(20),
    email: z.string().email().max(100),
    nombre_contacto: z.string().min(1).max(100),
    telefono_contacto: z.string().max(20).optional(),
    tipo_cliente: TipoClienteSchema.default('persona_natural'),
    razon_social: z.string().max(255).optional(),
    representante_legal: z.string().max(100).optional(),
    observaciones_despacho: z.string().optional(),
    // Acepta legacy `number[]` y se transforma a `{ tipos: number[] }`, o el shape v2
    // canónico `{ tipos, opciones?, bebidas? }`. El backend persiste lo que llega
    // (JSON.stringify) y el filtro de Armar Pedido lo lee con parseCategoriasAccesoV2.
    categorias_acceso: CategoriasAccesoInputSchema.optional(),
    // El vinculo con el usuario que accede al portal. Reemplaza al viejo
    // `crear_usuario`, una casilla que el backend validaba y nunca usaba.
    usuario_id: z.number().int().positive().nullable().optional(),
    activo: z.boolean().default(true),
})
    .refine((data) => {
    // Si es jurídica, razon_social es requerida
    if (data.tipo_cliente === 'juridica') {
        return data.razon_social && data.razon_social.length > 0;
    }
    return true;
}, {
    message: 'La razón social es requerida para clientes jurídicos',
    path: ['razon_social'],
});
// Schema para Dirección de Cliente
export const ClienteDireccionSchema = z.object({
    id: z.number().int().positive(),
    cliente_id: z.number().int().positive(),
    nombre_direccion: z.string().min(1).max(100),
    direccion: z.string().min(1),
    comuna: z.string().max(50).optional(),
    ciudad: z.string().max(50).optional(),
    codigo_postal: z.string().max(10).optional(),
    referencia: z.string().optional(),
    contacto_nombre: z.string().max(100).optional(),
    contacto_telefono: z.string().max(20).optional(),
    es_principal: z.boolean(),
    activa: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearClienteDireccionSchema = z.object({
    cliente_id: z.number().int().positive(),
    nombre_direccion: z.string().min(1).max(100),
    direccion: z.string().min(1),
    comuna: z.string().max(50).optional(),
    ciudad: z.string().max(50).optional(),
    codigo_postal: z.string().max(10).optional(),
    referencia: z.string().optional(),
    contacto_nombre: z.string().max(100).optional(),
    contacto_telefono: z.string().max(20).optional(),
    es_principal: z.boolean().default(false),
    activa: z.boolean().default(true),
});
// Schema para Acceso a Categorías de Cliente
export const ClienteCategoriaAccesoSchema = z.object({
    id: z.number().int().positive(),
    cliente_id: z.number().int().positive(),
    categoria_receta_id: z.number().int().positive(),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearClienteCategoriaAccesoSchema = z.object({
    cliente_id: z.number().int().positive(),
    categoria_receta_id: z.number().int().positive(),
    activo: z.boolean().default(true),
});
// Schema para Pedido
export const PedidoSchema = z.object({
    id: z.number().int().positive(),
    cliente_id: z.number().int().positive(),
    minuta_id: z.number().int().positive(),
    fecha_entrega: z.date(),
    estado: EstadoPedidoSchema,
    total: z.number().min(0),
    observaciones: z.string().max(1000).optional(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearPedidoSchema = z.object({
    cliente_id: z.number().int().positive(),
    minuta_id: z.number().int().positive(),
    fecha_entrega: z.date(),
    observaciones: z.string().max(1000).optional(),
    estado: EstadoPedidoSchema.default('reservado'),
});
// Schema para OrdenCompra
export const OrdenCompraSchema = z.object({
    id: z.number().int().positive(),
    numero_orden: z.string().min(1).max(50),
    proveedor: z.string().min(1).max(255),
    fecha_solicitud: z.date(),
    fecha_entrega_esperada: z.date().optional(),
    estado: EstadoCompraSchema,
    total: z.number().min(0),
    observaciones: z.string().max(1000).optional(),
    solicitado_por: z.number().int().positive(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearOrdenCompraSchema = z.object({
    numero_orden: z.string().min(1).max(50),
    proveedor: z.string().min(1).max(255),
    fecha_solicitud: z.date(),
    fecha_entrega_esperada: z.date().optional(),
    observaciones: z.string().max(1000).optional(),
    solicitado_por: z.number().int().positive(),
    estado: EstadoCompraSchema.default('solicitada'),
});
// =====================================================
// SCHEMAS PARA SISTEMA DE ROLES Y PERMISOS
// =====================================================
// Schema para Módulo del Sistema
export const ModuloSistemaSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(100),
    nombre_display: z.string().min(1).max(100),
    descripcion: z.string().optional(),
    icono: z.string().max(50).optional(),
    ruta: z.string().max(255).optional(),
    orden: z.number().int().default(0),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearModuloSistemaSchema = z.object({
    nombre: z.string().min(1).max(100),
    nombre_display: z.string().min(1).max(100),
    descripcion: z.string().optional(),
    icono: z.string().max(50).optional(),
    ruta: z.string().max(255).optional(),
    orden: z.number().int().default(0),
    activo: z.boolean().default(true),
});
// Schema para Permiso
export const PermisoSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(50),
    nombre_display: z.string().min(1).max(50),
    descripcion: z.string().optional(),
    created_at: z.date(),
    updated_at: z.date(),
});
// Schema para Rol del Sistema
export const RolSistemaSchema = z.object({
    id: z.number().int().positive(),
    nombre: z.string().min(1).max(100),
    nombre_display: z.string().min(1).max(100),
    descripcion: z.string().optional(),
    es_rol_base: z.boolean(),
    rol_base_id: z.number().int().positive().optional(),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
export const CrearRolSistemaSchema = z.object({
    nombre: z
        .string()
        .min(2, 'El nombre debe tener al menos 2 caracteres')
        .max(100)
        .regex(/^[a-z_]+$/, 'Solo minúsculas y guiones bajos (ej: supervisor_ventas)'),
    nombre_display: z
        .string()
        .min(2, 'El nombre debe tener al menos 2 caracteres')
        .max(100),
    descripcion: z.string().max(500).optional(),
    rol_base_id: z.number().int().positive().optional(),
    activo: z.boolean().default(true),
});
export const ActualizarRolSistemaSchema = z.object({
    nombre_display: z.string().min(2).max(100).optional(),
    descripcion: z.string().max(500).optional(),
    activo: z.boolean().optional(),
});
// Schema para Rol-Permiso
export const RolPermisoSchema = z.object({
    id: z.number().int().positive(),
    rol_id: z.number().int().positive(),
    modulo_id: z.number().int().positive(),
    permiso_id: z.number().int().positive(),
    activo: z.boolean(),
    created_at: z.date(),
    updated_at: z.date(),
});
// Schema para crear un rol con permisos
export const CrearRolConPermisosSchema = z.object({
    nombre: z
        .string()
        .min(2)
        .max(100)
        .regex(/^[a-z_]+$/, 'Solo minúsculas y guiones bajos'),
    nombre_display: z.string().min(2).max(100),
    descripcion: z.string().max(500).optional(),
    rol_base_id: z.number().int().positive().optional(),
    permisos: z.array(z.object({
        modulo_id: z.number().int().positive(),
        permiso_ids: z.array(z.number().int().positive()).min(1),
    })),
});
// Schema para actualizar permisos de un rol
export const ActualizarPermisosRolSchema = z.object({
    rol_id: z.number().int().positive(),
    permisos: z.array(z.object({
        modulo_id: z.number().int().positive(),
        permiso_ids: z.array(z.number().int().positive()),
    })),
});
// Schema para Usuario actualizado (con rol_id)
export const CrearUsuarioConRolSchema = z.object({
    nombre: z
        .string()
        .min(2, 'El nombre debe tener al menos 2 caracteres')
        .max(255),
    email: z.string().email('Email inválido').max(255),
    password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
    rol_id: z.number().int().positive('Debe seleccionar un rol válido'),
    activo: z.boolean().default(true),
});
export const ActualizarUsuarioSchema = z.object({
    nombre: z.string().min(2).max(255).optional(),
    email: z.string().email().max(255).optional(),
    password: z.string().min(6).optional(),
    rol_id: z.number().int().positive().optional(),
    activo: z.boolean().optional(),
});
// Schema para resetear contraseña
export const ResetearPasswordSchema = z.object({
    usuario_id: z.number().int().positive(),
    nueva_password: z
        .string()
        .min(6, 'La contraseña debe tener al menos 6 caracteres'),
});
// Schema para asignar permisos individuales
export const AsignarPermisoSchema = z.object({
    rol_id: z.number().int().positive(),
    modulo_id: z.number().int().positive(),
    permiso_id: z.number().int().positive(),
    activo: z.boolean().default(true),
});
// Schema para validar tipos de permiso
export const TipoPermisoSchema = z.enum(['ver', 'crear', 'editar', 'eliminar']);
// =====================================================
// MÓDULO DE CAPACITACIÓN
// =====================================================
// `encargado_inventario` agregado 2026-09-08. OJO: estos roles son los del
// modulo de capacitacion (training_users.rol), NO los del sistema principal
// (tabla `roles`). Coincide el nombre a proposito para que sea evidente a
// quien aplica cada modulo.
export const TrainingRolSchema = z.enum([
    'administrador',
    'operador',
    'supervisor',
    'encargado_inventario',
]);
export const AttemptStatusSchema = z.enum(['in_progress', 'passed', 'failed']);
export const RespuestaLetraSchema = z.enum(['a', 'b', 'c', 'd']);
export const RegistrarTrainingUserSchema = z.object({
    nombre: z.string().min(2, 'El nombre es requerido').max(255),
    email: z.string().email('Email inválido').max(255),
    empresa: z.string().min(1, 'La empresa es requerida').max(255),
    rol: TrainingRolSchema,
    telefono: z.string().max(50).optional().nullable(),
});
export const IniciarIntentoSchema = z.object({
    usuarioId: z.number().int().positive(),
    moduloId: z.number().int().positive(),
});
export const EnviarRespuestasSchema = z.object({
    respuestas: z
        .array(z.object({
        preguntaId: z.number().int().positive(),
        respuesta: RespuestaLetraSchema,
    }))
        .min(1, 'Debe enviar al menos una respuesta')
        .max(10, 'Máximo 10 respuestas por intento'),
});
export const TrainingOpcionSchema = z.object({
    id: RespuestaLetraSchema,
    texto: z.string().min(1).max(500),
});
export const CrearTrainingModuleSchema = z.object({
    slug: z
        .string()
        .min(2)
        .max(100)
        .regex(/^[a-z0-9-]+$/, 'Slug inválido (usar minúsculas, números y guiones)'),
    nombre: z.string().min(2).max(255),
    descripcion: z.string().min(1),
    orden: z.number().int().min(0).default(0),
    video_url: z.string().url().max(500).optional().nullable(),
    manual_url: z.string().url().max(500).optional().nullable(),
    manual_nombre: z.string().max(255).optional().nullable(),
    umbral_aprobacion: z.number().int().min(0).max(100).default(85),
    tiempo_reintento_horas: z.number().int().min(0).default(2),
    roles_aplicables: z.array(TrainingRolSchema).min(1),
    activo: z.boolean().default(true),
});
export const ActualizarTrainingModuleSchema = CrearTrainingModuleSchema.partial();
export const CrearTrainingQuestionSchema = z.object({
    modulo_id: z.number().int().positive(),
    pregunta: z.string().min(1).max(1000),
    opciones: z.array(TrainingOpcionSchema).length(4, 'Deben ser 4 opciones'),
    respuesta_correcta: RespuestaLetraSchema,
    explicacion: z.string().max(2000).optional().nullable(),
    activa: z.boolean().default(true),
});
export const ActualizarTrainingQuestionSchema = CrearTrainingQuestionSchema.partial();
